import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut, Package, ShoppingBag, Mail, Plus, Trash2, Edit3, Save, X, Star, Percent, Image as ImageIcon, Ticket, ToggleLeft, ToggleRight } from 'lucide-react';
import { bevAdminApi, setAdminToken, clearAdminToken, hasAdminToken } from '../lib/bevApi';

const LOGO_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_d4801e57-518e-48e3-b04b-23ec30a7a74e/artifacts/e12nnw5g_New%20Bev%27s%20Furniture%20Logo.png';
const money = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const emptyProduct = {
  sku: '', name: '', category: 'Upholstery', subcategory: '',
  description: '', dimensions: '', price: 0, original_price: null,
  on_sale: false, sale_label: '', status: 'available', featured: false, images: [],
};

const CATEGORIES = ['Upholstery', 'Motion Upholstery', 'Sectionals', 'Recliners', 'Bedroom', 'Dining', 'Mattresses', 'Accessories'];
const STATUSES = ['available', 'sold_out', 'coming_soon', 'hidden'];

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('romero.sumpter@gmail.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { access_token } = await bevAdminApi.login(email, password);
      setAdminToken(access_token);
      onLogin();
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bev-admin-login" data-testid="bev-admin-login">
      <div className="bev-admin-login-card">
        <img src={LOGO_URL} alt="Bev's Furniture" className="bev-admin-login-logo" />
        <h1>Admin Portal</h1>
        <p>Sign in to manage products and orders.</p>
        <form onSubmit={submit}>
          <label><span>Email</span><input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="bev-admin-email" required /></label>
          <label><span>Password</span><input type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="bev-admin-password" required /></label>
          {error && <p className="bev-form-err">{error}</p>}
          <button type="submit" className="bev-btn-primary bev-btn-large" disabled={loading} data-testid="bev-admin-login-submit">
            <LogIn size={16} /> {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

const ProductForm = ({ product, onSave, onCancel }) => {
  const [form, setForm] = useState(product || emptyProduct);
  const [imgUrl, setImgUrl] = useState('');

  const set = (k, v) => setForm({ ...form, [k]: v });
  const addImage = () => {
    if (imgUrl.trim()) {
      set('images', [...(form.images || []), imgUrl.trim()]);
      setImgUrl('');
    }
  };
  const removeImage = (i) => set('images', form.images.filter((_, idx) => idx !== i));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      price: Number(form.price) || 0,
      original_price: form.original_price ? Number(form.original_price) : null,
    });
  };

  return (
    <form className="bev-admin-form" onSubmit={handleSubmit} data-testid="bev-product-form">
      <div className="bev-admin-form-grid">
        <label><span>SKU *</span><input required value={form.sku} onChange={e => set('sku', e.target.value)} /></label>
        <label className="span-2"><span>Product Name *</span><input required value={form.name} onChange={e => set('name', e.target.value)} /></label>
        <label>
          <span>Category *</span>
          <select value={form.category} onChange={e => set('category', e.target.value)}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label><span>Subcategory</span><input value={form.subcategory || ''} onChange={e => set('subcategory', e.target.value)} placeholder="e.g., Sofas, Reclining Sofas" /></label>
        <label><span>Dimensions</span><input value={form.dimensions || ''} onChange={e => set('dimensions', e.target.value)} placeholder='e.g., 88" W × 38" D × 38" H' /></label>
        <label className="span-3"><span>Description</span><textarea rows="3" value={form.description} onChange={e => set('description', e.target.value)} /></label>

        <label><span>Price ($) *</span><input required type="number" step="1" value={form.price} onChange={e => set('price', e.target.value)} /></label>
        <label>
          <span>Status</span>
          <select value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </label>
        <label className="bev-check">
          <input type="checkbox" checked={form.featured || false} onChange={e => set('featured', e.target.checked)} />
          <span><Star size={12} /> Featured</span>
        </label>

        <label className="bev-check span-3">
          <input type="checkbox" checked={form.on_sale || false} onChange={e => set('on_sale', e.target.checked)} />
          <span><Percent size={12} /> On Sale</span>
        </label>

        {form.on_sale && (
          <>
            <label><span>Original Price ($)</span><input type="number" step="1" value={form.original_price || ''} onChange={e => set('original_price', e.target.value)} /></label>
            <label className="span-2"><span>Sale Label</span><input value={form.sale_label || ''} onChange={e => set('sale_label', e.target.value)} placeholder="e.g., 20% OFF, SAVE $200" /></label>
          </>
        )}

        <div className="span-3 bev-images-block">
          <label><span><ImageIcon size={12} /> Product Images</span></label>
          <div className="bev-image-add">
            <input value={imgUrl} onChange={e => setImgUrl(e.target.value)} placeholder="Paste image URL and click Add" />
            <button type="button" onClick={addImage} className="bev-btn-secondary"><Plus size={14} /> Add</button>
          </div>
          {form.images && form.images.length > 0 && (
            <div className="bev-image-list">
              {form.images.map((src, i) => (
                <div key={i} className="bev-image-item">
                  <img src={src} alt="" />
                  <button type="button" onClick={() => removeImage(i)}><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="bev-admin-form-actions">
        <button type="button" className="bev-btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
        <button type="submit" className="bev-btn-primary" data-testid="bev-product-save"><Save size={14} /> Save Product</button>
      </div>
    </form>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);  // 'new' | product object

  const load = () => bevAdminApi.listProducts().then(setProducts);
  useEffect(() => { load(); }, []);

  const handleSave = async (data) => {
    if (editing === 'new') {
      await bevAdminApi.createProduct(data);
    } else {
      const { id, created_at, updated_at, ...update } = data;
      await bevAdminApi.updateProduct(editing.id, update);
    }
    setEditing(null);
    load();
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    await bevAdminApi.deleteProduct(id);
    load();
  };
  const quickToggleSale = async (p) => {
    await bevAdminApi.updateProduct(p.id, { on_sale: !p.on_sale });
    load();
  };
  const quickToggleStatus = async (p) => {
    const next = p.status === 'available' ? 'sold_out' : 'available';
    await bevAdminApi.updateProduct(p.id, { status: next });
    load();
  };
  const quickEditPrice = async (p) => {
    const newPrice = window.prompt(`New price for ${p.name}:`, p.price);
    if (newPrice === null) return;
    const price = Number(newPrice);
    if (Number.isNaN(price) || price < 0) return alert('Invalid price');
    await bevAdminApi.updateProduct(p.id, { price });
    load();
  };

  if (editing !== null) {
    return (
      <div>
        <h2 className="bev-admin-h2">{editing === 'new' ? 'Add New Product' : `Edit — ${editing.name}`}</h2>
        <ProductForm
          product={editing === 'new' ? null : editing}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="bev-admin-toolbar">
        <h2 className="bev-admin-h2">Products ({products.length})</h2>
        <button className="bev-btn-primary" onClick={() => setEditing('new')} data-testid="bev-add-product-btn">
          <Plus size={14} /> Add Product
        </button>
      </div>
      <div className="bev-admin-table">
        <div className="bev-admin-row bev-admin-head">
          <div>Image</div><div>Product</div><div>Category</div><div>Price</div><div>Status</div><div>Actions</div>
        </div>
        {products.map(p => (
          <div key={p.id} className="bev-admin-row" data-testid={`bev-admin-row-${p.sku}`}>
            <div>
              {p.images && p.images[0] ? <img src={p.images[0]} alt="" className="bev-admin-thumb" /> : <div className="bev-admin-thumb bev-empty-thumb" />}
            </div>
            <div>
              <strong>{p.name}</strong>
              <span className="bev-admin-sku">{p.sku}</span>
              {p.featured && <span className="bev-admin-badge bev-badge-featured">★ Featured</span>}
              {p.on_sale && <span className="bev-admin-badge bev-badge-sale">Sale</span>}
            </div>
            <div>{p.category}{p.subcategory && ` • ${p.subcategory}`}</div>
            <div>
              <button className="bev-admin-price-btn" onClick={() => quickEditPrice(p)} title="Click to change price">
                {money(p.price)}
              </button>
              {p.on_sale && p.original_price && <span className="bev-admin-orig">{money(p.original_price)}</span>}
            </div>
            <div>
              <button className={`bev-admin-status bev-status-${p.status}`} onClick={() => quickToggleStatus(p)}>
                {p.status.replace('_', ' ')}
              </button>
            </div>
            <div className="bev-admin-actions">
              <button title={p.on_sale ? 'Turn off sale' : 'Put on sale'} onClick={() => quickToggleSale(p)}>
                <Percent size={14} />
              </button>
              <button title="Edit" onClick={() => setEditing(p)}><Edit3 size={14} /></button>
              <button title="Delete" onClick={() => handleDelete(p.id)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  useEffect(() => { bevAdminApi.listOrders().then(setOrders); }, []);
  return (
    <div>
      <h2 className="bev-admin-h2">Orders ({orders.length})</h2>
      {orders.length === 0 ? (
        <p className="bev-empty">No orders yet — they&apos;ll show up here as soon as a customer completes checkout.</p>
      ) : (
        <div className="bev-admin-orders">
          {orders.map(o => (
            <div key={o.id} className="bev-admin-order" data-testid={`bev-admin-order-${o.id.slice(0,8)}`}>
              <div className="bev-admin-order-head">
                <div>
                  <strong>{o.product_snapshot.name}</strong>
                  <span className={`bev-admin-status bev-status-${o.status}`}>{o.status}</span>
                </div>
                <div className="bev-admin-order-total">{money(o.total)}</div>
              </div>
              <div className="bev-admin-order-body">
                <div>
                  <span className="bev-admin-label">Customer</span>
                  <p>{o.customer.name}<br />{o.customer.email}<br />{o.customer.phone}</p>
                </div>
                <div>
                  <span className="bev-admin-label">Deliver to</span>
                  <p>{o.customer.street}<br />{o.customer.city}, {o.customer.state} {o.customer.zip}</p>
                  {o.customer.notes && <p><em>Note: {o.customer.notes}</em></p>}
                </div>
                <div>
                  <span className="bev-admin-label">Payment</span>
                  <p>Subtotal: {money(o.subtotal)}<br />Delivery: {money(o.shipping_fee)}<br /><strong>Total: {money(o.total)}</strong></p>
                  {o.paypal_capture_id && <p className="bev-admin-capture">PayPal: {o.paypal_capture_id}</p>}
                </div>
                <div>
                  <span className="bev-admin-label">Received</span>
                  <p>{new Date(o.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InquiriesTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { bevAdminApi.listInquiries().then(setItems); }, []);
  return (
    <div>
      <h2 className="bev-admin-h2">Inquiries ({items.length})</h2>
      {items.length === 0 ? (
        <p className="bev-empty">No inquiries yet.</p>
      ) : (
        <div className="bev-admin-orders">
          {items.map(q => (
            <div key={q.id} className="bev-admin-order">
              <div className="bev-admin-order-head">
                <div><strong>{q.name}</strong> <span className="bev-admin-sku">{q.email}</span></div>
                <div className="bev-admin-order-total" style={{fontSize:'13px',fontWeight:500,color:'#8b7a5a'}}>{new Date(q.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{padding:'0 20px 16px'}}>
                <p>{q.message}</p>
                {q.phone && <p className="bev-admin-capture">📞 {q.phone}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyCoupon = {
  code: '',
  discount_type: 'percent',
  discount_value: 10,
  min_order: null,
  max_uses: null,
  active: true,
  expires_at: null,
  description: '',
};

const CouponForm = ({ coupon, onSave, onCancel }) => {
  const [form, setForm] = useState(coupon || emptyCoupon);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      code: form.code.trim().toUpperCase(),
      discount_value: Number(form.discount_value),
      min_order: form.min_order ? Number(form.min_order) : null,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      expires_at: form.expires_at || null,
    });
  };

  return (
    <form className="bev-admin-form" onSubmit={submit} data-testid="bev-coupon-form">
      <div className="bev-admin-form-grid">
        <label>
          <span>Coupon Code *</span>
          <input
            required
            value={form.code}
            onChange={e => set('code', e.target.value.toUpperCase())}
            placeholder="e.g., SUMMER25"
            data-testid="bev-coupon-code"
          />
        </label>
        <label>
          <span>Type *</span>
          <select value={form.discount_type} onChange={e => set('discount_type', e.target.value)}>
            <option value="percent">Percent (%)</option>
            <option value="amount">Amount ($)</option>
          </select>
        </label>
        <label>
          <span>{form.discount_type === 'percent' ? 'Percent Off' : 'Dollars Off'} *</span>
          <input
            required
            type="number"
            step="1"
            min="0"
            value={form.discount_value}
            onChange={e => set('discount_value', e.target.value)}
            data-testid="bev-coupon-value"
          />
        </label>
        <label className="span-3">
          <span>Description (shown to customer)</span>
          <input
            value={form.description || ''}
            onChange={e => set('description', e.target.value)}
            placeholder="e.g., 25% off summer sale"
          />
        </label>
        <label>
          <span>Min Order ($)</span>
          <input
            type="number"
            step="1"
            value={form.min_order || ''}
            onChange={e => set('min_order', e.target.value)}
            placeholder="Optional"
          />
        </label>
        <label>
          <span>Max Uses</span>
          <input
            type="number"
            step="1"
            value={form.max_uses || ''}
            onChange={e => set('max_uses', e.target.value)}
            placeholder="Unlimited"
          />
        </label>
        <label>
          <span>Expires (optional)</span>
          <input
            type="date"
            value={form.expires_at ? form.expires_at.slice(0, 10) : ''}
            onChange={e => set('expires_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
          />
        </label>
        <label className="bev-check span-3">
          <input type="checkbox" checked={form.active !== false} onChange={e => set('active', e.target.checked)} />
          <span>Active (customers can redeem)</span>
        </label>
      </div>
      <div className="bev-admin-form-actions">
        <button type="button" className="bev-btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
        <button type="submit" className="bev-btn-primary" data-testid="bev-coupon-save"><Save size={14} /> Save Coupon</button>
      </div>
    </form>
  );
};

const CouponsTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = () => bevAdminApi.listCoupons().then(setItems);
  useEffect(() => { load(); }, []);

  const handleSave = async (data) => {
    setError('');
    try {
      if (editing === 'new') {
        await bevAdminApi.createCoupon(data);
      } else {
        const { id, created_at, used_count, ...update } = data;
        await bevAdminApi.updateCoupon(editing.id, update);
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save coupon.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon? Any customer using it right now will lose the discount.')) return;
    await bevAdminApi.deleteCoupon(id);
    load();
  };

  const toggleActive = async (c) => {
    await bevAdminApi.updateCoupon(c.id, { active: !c.active });
    load();
  };

  if (editing !== null) {
    return (
      <div>
        <h2 className="bev-admin-h2">{editing === 'new' ? 'Add Coupon' : `Edit — ${editing.code}`}</h2>
        {error && <p className="bev-form-err">{error}</p>}
        <CouponForm
          coupon={editing === 'new' ? null : editing}
          onSave={handleSave}
          onCancel={() => { setEditing(null); setError(''); }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="bev-admin-toolbar">
        <h2 className="bev-admin-h2">Coupons ({items.length})</h2>
        <button className="bev-btn-primary" onClick={() => setEditing('new')} data-testid="bev-add-coupon-btn">
          <Plus size={14} /> Add Coupon
        </button>
      </div>
      {items.length === 0 ? (
        <p className="bev-empty">No coupon codes yet. Create your first one to give customers a discount at checkout.</p>
      ) : (
        <div className="bev-admin-table">
          <div className="bev-admin-row bev-admin-row-coupons bev-admin-head">
            <div>Code</div><div>Discount</div><div>Usage</div><div>Expires</div><div>Status</div><div>Actions</div>
          </div>
          {items.map(c => {
            const expired = c.expires_at && new Date(c.expires_at) < new Date();
            const exhausted = c.max_uses != null && c.used_count >= c.max_uses;
            return (
              <div key={c.id} className="bev-admin-row bev-admin-row-coupons" data-testid={`bev-coupon-row-${c.code}`}>
                <div>
                  <strong style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>{c.code}</strong>
                  {c.description && <span className="bev-admin-sku" style={{ textTransform: 'none' }}>{c.description}</span>}
                </div>
                <div>
                  <strong style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: '18px', color: 'var(--bev-navy)' }}>
                    {c.discount_type === 'percent' ? `${c.discount_value}%` : `$${c.discount_value}`}
                  </strong>
                  {c.min_order && <span className="bev-admin-sku" style={{ textTransform: 'none' }}>min ${c.min_order}</span>}
                </div>
                <div>
                  {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ' uses'}
                </div>
                <div>
                  {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                </div>
                <div>
                  <button
                    className={`bev-admin-status bev-status-${(c.active && !expired && !exhausted) ? 'available' : 'hidden'}`}
                    onClick={() => toggleActive(c)}
                    title="Click to toggle"
                  >
                    {expired ? 'expired' : exhausted ? 'used up' : c.active ? 'active' : 'off'}
                  </button>
                </div>
                <div className="bev-admin-actions">
                  <button title={c.active ? 'Deactivate' : 'Activate'} onClick={() => toggleActive(c)}>
                    {c.active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  </button>
                  <button title="Edit" onClick={() => setEditing(c)}><Edit3 size={14} /></button>
                  <button title="Delete" onClick={() => handleDelete(c.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const BevsAdminPage = () => {
  const [authed, setAuthed] = useState(hasAdminToken());
  const [tab, setTab] = useState('products');
  const navigate = useNavigate();

  useEffect(() => {
    if (authed) {
      bevAdminApi.me().catch(() => {
        clearAdminToken();
        setAuthed(false);
      });
    }
  }, [authed]);

  if (!authed) {
    return <AdminLogin onLogin={() => setAuthed(true)} />;
  }

  const logout = () => { clearAdminToken(); setAuthed(false); navigate('/'); };

  return (
    <div className="bev-admin-page" data-testid="bev-admin-page">
      <header className="bev-admin-header">
        <div className="bev-admin-header-left">
          <img src={LOGO_URL} alt="Bev's Furniture" className="bev-admin-logo" />
          <span className="bev-admin-badge-nav">Admin Portal</span>
        </div>
        <nav className="bev-admin-nav">
          <button className={tab === 'products' ? 'is-active' : ''} onClick={() => setTab('products')} data-testid="bev-tab-products"><Package size={14} /> Products</button>
          <button className={tab === 'orders' ? 'is-active' : ''} onClick={() => setTab('orders')} data-testid="bev-tab-orders"><ShoppingBag size={14} /> Orders</button>
          <button className={tab === 'coupons' ? 'is-active' : ''} onClick={() => setTab('coupons')} data-testid="bev-tab-coupons"><Ticket size={14} /> Coupons</button>
          <button className={tab === 'inquiries' ? 'is-active' : ''} onClick={() => setTab('inquiries')} data-testid="bev-tab-inquiries"><Mail size={14} /> Inquiries</button>
        </nav>
        <button onClick={logout} className="bev-btn-ghost" data-testid="bev-admin-logout"><LogOut size={14} /> Sign out</button>
      </header>
      <main className="bev-admin-main">
        {tab === 'products' && <ProductsTab />}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'coupons' && <CouponsTab />}
        {tab === 'inquiries' && <InquiriesTab />}
      </main>
    </div>
  );
};

export default BevsAdminPage;
